import React, { useEffect, useRef, useState } from "react";
import { Input, InputGroup, toaster, Toggle } from "rsuite";
import moment, { Moment } from "moment";
import { extractTimezoneFromISO } from "../ExternalDevices/components/Dimmer/utils/Dimmer.utils";
import NotificationFactory from "../../../shared/Notifications";
import { formatTimezoneOffset } from "../../../shared/Time.utils";
import { ClockStation } from "../../../Targets";
import { ReadShortCircuitControlState, WriteShortCircuitControlState } from "../../../Commands";
import { PrimaryClockApi } from "../../../services/PrimaryClock.api";
import "./PrimaryClock.scss";
import DateTimeEditModal from "../../modals/DateTimeEditModal/DateTimeEditModal";
import UpdateButton from "../../shared/UpdateButton/UpdateButton";
import DuskDawnTimeControl from "../../shared/DuskDawnTimeControl/DuskDawnTimeControl";
import {TfiSaveIcon, VscEditIcon} from "../../../models/Icons.model";
import {ClockTimeData, CommandResult, DeviceData, DuskDawnHandle, PrimaryClockProps} from "./PrimaryClock.model";

const PrimaryClock = ({
  deviceId,
  executeCommand,
  isClockStation,
  duskTime,
  dawnTime,
  getDuskDawnTime
}: PrimaryClockProps) => {
  const [loading, setLoading] = useState(false);
  const [deviceData, setDeviceData] = useState<DeviceData | null>(null);
  const [deviceTime, setDeviceTime] = useState<Moment | Date | string | null>(null);
  const [userTimezone, setUserTimezone] = useState<string | null>(null);
  const [currentUtcDateTime, setCurrentUtcDateTime] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const timeMomentRef = useRef<Moment>(moment());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [shortCircuitControl, setShortCircuitControl] = useState<boolean | null>(null);
  const [clockTimeData, setClockTimeData] = useState<ClockTimeData | null>(null);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const duskDawnRef = useRef<DuskDawnHandle | null>(null);

  useEffect(() => {
    if (deviceId !== null && deviceId !== undefined) {
      getShortCircuitControl().then();
      getClockTime(deviceId).then();
    }
  }, [deviceId, isClockStation]);

  const getClockTime = async (id: string | number) => {
    setLoading(true);
    try {
      const result = (await PrimaryClockApi.getTime(id as string)) as ClockTimeData;

      if (result?.time) {
        setClockTimeData(result);

        const wallTime = result.time.split("+")[0];
        const timeMoment = moment(wallTime, "YYYY-MM-DDTHH:mm:ss");

        setDeviceTime(timeMoment);
        timeMomentRef.current = timeMoment.clone();
        setCurrentTime(timeMoment.format("DD.MM.YYYY HH:mm:ss"));

        const timezone = extractTimezoneFromISO(result.time);
        if (timezone) {
          setUserTimezone(timezone);
        }
      }
    } catch (e) {
      console.error("Ошибка получения времени:", e);
      toaster.push(
        NotificationFactory.error("Ошибка получения времени"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const updateClockTime = async (time: Moment | Date | string) => {
    setLoading(true);
    try {
      const formattedTime = moment(time).format();

      const result = await PrimaryClockApi.updateTime(deviceId as string, formattedTime);

      if (result) {
        toaster.push(
          NotificationFactory.success("Время успешно обновлено"),
          { placement: "topEnd" }
        );
        await getClockTime(deviceId);
      } else {
        throw new Error("Не удалось обновить время");
      }
    } catch (e) {
      console.error("Ошибка при сохранении времени:", e);
      toaster.push(
        NotificationFactory.error("Ошибка при сохранении времени"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!deviceTime) {
      return;
    }

    updateTimeFromDeviceTime();

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (timeMomentRef.current) {
        timeMomentRef.current.add(1, "seconds");
        setCurrentTime(timeMomentRef.current.format("DD.MM.YYYY HH:mm:ss"));
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [deviceTime]);

  useEffect(() => {
    setCurrentUtcDateTime(getCurrentUtcDateTime());
    const utcIntervalId = setInterval(() => {
      setCurrentUtcDateTime(getCurrentUtcDateTime());
    }, 1000);

    return () => clearInterval(utcIntervalId);
  }, []);

  const updateTimeFromDeviceTime = () => {
    if (deviceTime) {
      const m = getTimeMoment(deviceTime);
      timeMomentRef.current = m;
      setCurrentTime(m.format("DD.MM.YYYY HH:mm:ss"));
    }
  };

  const getTimeMoment = (date: Moment | Date | string | null) => {
    if (!date) return moment();

    try {
      if (typeof date === "string") {
        return moment(date, "DD.MM.YYYY HH:mm:ss");
      }

      if (date instanceof Date) {
        return moment(date);
      }

      if (moment.isMoment(date)) {
        return date.clone();
      }

      return moment();
    } catch (error) {
      console.error("Error creating moment:", error);
      return moment();
    }
  };

  const getCurrentUtcDateTime = () => {
    return moment().utc().format("DD.MM.YYYY HH:mm:ss");
  };

  const handleTimezoneChange = (value: string) => {
    if (/^[+-]?\d*$/.test(value)) {
      setUserTimezone(value);
    }
  };

  const onSaveTimezone = async () => {
    setLoading(true);
    try {
      const formattedTimezone = formatTimezoneOffset(userTimezone ?? "");
      const success = await PrimaryClockApi.updateTimezone(deviceId as string, formattedTimezone);

      if (success) {
        toaster.push(
          NotificationFactory.success("Часовой пояс успешно обновлен"),
          { placement: "topEnd" }
        );
        await getClockTime(deviceId);
      } else {
        throw new Error("Не удалось обновить часовой пояс");
      }
    } catch (e) {
      console.error("Ошибка при сохранении часового пояса:", e);
      toaster.push(
        NotificationFactory.error("Ошибка при сохранении часового пояса"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const updateShortCircuitControl = async (enabled: boolean) => {
    setLoading(true);

    try {
      if (executeCommand) {
        await executeCommand(ClockStation, WriteShortCircuitControlState, [Number(enabled)]);
      }
      getShortCircuitControl().then();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getShortCircuitControl = async () => {
    setLoading(true);

    try {
      if (!executeCommand) return;
      const result = (await executeCommand(ClockStation, ReadShortCircuitControlState, [])) as CommandResult;
      setShortCircuitControl(Boolean(result.Params[0]));

    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshClockData = async () => {
    await getClockTime(deviceId);
    if (getDuskDawnTime) {
      await getDuskDawnTime();
    }

    if (duskDawnRef.current?.refresh) {
      duskDawnRef.current.refresh();
    }

    toaster.push(
      NotificationFactory.success("Данные часов обновлены"),
      { placement: "topEnd" }
    );
  };

  const getFormattedDateParts = () => {
    if (!clockTimeData?.time) {
      return {
        day: "",
        month: "",
        year: "",
        weekday: ""
      };
    }

    const timeMoment = moment(clockTimeData.time);

    return {
      day: timeMoment.format("DD"),
      month: timeMoment.locale("ru").format("MMMM"),
      year: timeMoment.format("YYYY"),
      weekday: timeMoment.locale("ru").format("dddd")
    };
  };

  const dateParts = getFormattedDateParts();

  return (
    <>
      <div className="fieldset-wrapper mb-4">
        <div className="legend-title main-legend-title">Первичные часы</div>
        <section className="primary-clock p-1 pb-2">
          <div className="row">
            <div className="col-md-12 text-end">
              <UpdateButton loading={!deviceTime} onRefresh={handleRefreshClockData} />
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col-md-4">
              <InputGroup style={{ marginBottom: "5px" }}>
                <InputGroup.Addon>Время и дата</InputGroup.Addon>
                <Input
                  className="bg-green"
                  readOnly={true}
                  value={currentTime}
                />
                <InputGroup.Addon>
                  <VscEditIcon
                    onClick={() => setIsTimeModalOpen(true)}
                    style={{ cursor: "pointer" }}
                    title="Редактировать"
                  />
                </InputGroup.Addon>
              </InputGroup>
            </div>
            <div className="col-md-3">
              <InputGroup style={{ width: 220 }} className="mb-1">
                <InputGroup.Addon>Час. пояс</InputGroup.Addon>
                <Input
                  value={userTimezone ?? ""}
                  onChange={handleTimezoneChange}
                  style={{ textAlign: "center" }}
                />
                <InputGroup.Addon>
                  <TfiSaveIcon
                    style={{ cursor: "pointer" }}
                    onClick={onSaveTimezone}
                    title="Сохранить часовой пояс"
                  />
                </InputGroup.Addon>
              </InputGroup>
            </div>
            <div className="col-md-4">
              <Toggle
                size="md"
                checked={deviceData?.isDaylightSavingEnabled || false}
                disabled={loading}
                loading={loading}
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
              >
                Авт. переход зима / лето
              </Toggle>
            </div>
          </div>
          <div className="row align-items-center mt-2">
            <div className="col-md-5">
              <InputGroup style={{ marginBottom: "5px" }}>
                <InputGroup.Addon>Время и дата UTC</InputGroup.Addon>
                <Input
                  className="bg-yellow"
                  readOnly={true}
                  value={currentUtcDateTime}
                />
              </InputGroup>
            </div>
            <div className="col-md-4">
              <DuskDawnTimeControl
                ref={duskDawnRef}
                duskTime={duskTime}
                dawnTime={dawnTime}
              />
            </div>
            <div className="col-md-3 text-center">
              <Toggle
                size="md"
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
                checked={!!shortCircuitControl}
                disabled={loading}
                loading={loading}
                onChange={(val) => updateShortCircuitControl(Boolean(val))}
              >
                Контроль КЗ
              </Toggle>
            </div>
          </div>
          <div className="row text-center align-items-center mt-2">
            <div className="col-md-3">
              <InputGroup>
                <InputGroup.Addon>День</InputGroup.Addon>
                <Input readOnly={true} value={dateParts.day} />
              </InputGroup>
            </div>
            <div className="col-md-3">
              <InputGroup>
                <InputGroup.Addon>Месяц</InputGroup.Addon>
                <Input readOnly={true} value={dateParts.month} />
              </InputGroup>
            </div>
            <div className="col-md-3">
              <InputGroup>
                <InputGroup.Addon>Год</InputGroup.Addon>
                <Input readOnly={true} value={dateParts.year} />
              </InputGroup>
            </div>
            <div className="col-md-3">
              <InputGroup>
                <InputGroup.Addon>День недели</InputGroup.Addon>
                <Input readOnly={true} value={dateParts.weekday} />
              </InputGroup>
            </div>
          </div>
        </section>
      </div>

      <DateTimeEditModal
        open={isTimeModalOpen}
        onClose={() => setIsTimeModalOpen(false)}
        initialValue={deviceTime as Moment}
        loading={loading}
        title="Редактировать дату и время"
        onSave={async (newMoment: Moment) => {
          setDeviceTime(newMoment);
          timeMomentRef.current = newMoment.clone();
          setCurrentTime(newMoment.format("HH:mm:ss"));

          await updateClockTime(newMoment);
          setIsTimeModalOpen(false);
        }}
      />

    </>
  );
};

export default PrimaryClock;
